import {useEffect, useState} from 'react';

import {useAppContext} from '../../hooks';

const STORAGE_KEY = 'sidebarNavShell';

/**
 * Whether this request should render the sidebar shell instead of the top
 * header. Logged-in users get the shell; visitors get the header.
 *
 * AppContext resolves the user asynchronously, so there is a window on every
 * load where we do not yet know which shell to draw. Rendering the logged-out
 * header during that window would visibly reflow the whole page a moment
 * later, so we remember the last answer and reuse it optimistically. Written
 * only once we know the real answer, so a logged-out visitor never inherits a
 * previous user's shell for more than one paint.
 */
const useSidebarNavEnabled = () => {
  const {currentUser, loading} = useAppContext();
  const [remembered, setRemembered] = useState(false);

  useEffect(() => {
    try {
      setRemembered(window.localStorage.getItem(STORAGE_KEY) === 'true');
    } catch (err) {
      // No storage available; we just fall back to the header while loading.
    }
  }, []);

  const enabled = Boolean(currentUser);

  useEffect(() => {
    if (loading) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(enabled));
    } catch (err) {
      // Preference simply won't be remembered.
    }
  }, [loading, enabled]);

  return loading ? remembered : enabled;
};

export default useSidebarNavEnabled;
